import React from 'react';
import { Lock } from 'lucide-react';
import { PROVISIONAL_REPORT_CTA } from '../lib/commercialCopy';

export interface ComplexityHints {
    hasTrusts?: boolean;
    hasLifetimeGifts?: boolean;
    hasMultipleProperties?: boolean;
    hasTenantsInCommon?: boolean;
    hasReliefAssets?: boolean;
    hasForeignAssets?: boolean;
    hasDirectDescendants?: boolean;
}

interface Props {
    grossEstateValue?: number | null;
    netEstateValue?: number | null;
    /** 'likely_taxable' | 'unlikely_taxable' | 'unknown' */
    thresholdStatus?: string | null;
    /** Server-rounded headline IHT figure. 0 = no tax, null = unknown. */
    estimatedIht?: number | null;
    /** Server-provided basic headline (auditResponse.summary for free users) */
    headline?: string;
    /** Contextual message from auditResponse.upgrade.message */
    upgradeMessage?: string;
    complexityHints?: ComplexityHints | null;
    /** WP1: server flag (derived.trust_estate_inclusion_unresolved) — trust property may be missing, nothing can be confirmed. */
    trustEstateInclusionUnresolved?: boolean;
    onUpgrade: () => void;
}

const fmtGBP = (n?: number | null): string =>
    n == null
        ? '—'
        : n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

/**
 * Free-tier replacement for the full engine-driven results block.
 *
 * Shows only:
 *   - gross & net estate values (the inputs the user gave us)
 *   - a high-level threshold status badge
 *   - a tension-aware headline (controlled uncertainty)
 *   - an optional contextual complexity line
 *
 * Designed to make clear what the user is missing without ever rendering
 * partial or NaN engine output.
 */
export const BasicEstateSummary: React.FC<Props> = ({
    grossEstateValue,
    netEstateValue,
    thresholdStatus,
    estimatedIht,
    headline,
    upgradeMessage,
    complexityHints,
    trustEstateInclusionUnresolved = false,
    onUpgrade,
}) => {
    const unresolved = trustEstateInclusionUnresolved === true;

    // ── Tension-aware headline (controlled uncertainty) ───────
    const tensionHeadline =
        unresolved
            ? 'Trust property may be missing from this estate calculation, so the inheritance tax position cannot be confirmed.'
            : thresholdStatus === 'likely_taxable'
            ? 'Your estate is likely above the inheritance tax threshold, but the exact amount depends on allowances, gifts, and trust structures.'
            : thresholdStatus === 'unlikely_taxable'
                ? 'Inheritance tax is unlikely to be due, but the final position depends on allowances, reliefs, and how assets pass to beneficiaries.'
                : 'Your estate may be affected by inheritance tax, but the final position depends on additional factors such as reliefs, gifts, and ownership structure.';

    // Unresolved: the component's own wording, so no reassuring headline can override it.
    const displayHeadline = unresolved ? tensionHeadline : (headline || tensionHeadline);

    // ── Headline tax estimate (free-tier "Do I owe tax?" answer) ──
    // An unresolved trust never shows a figure or a "no tax" answer, whatever else arrives.
    const estimateLabel: string =
        unresolved
            ? 'Estimated inheritance tax: Cannot be confirmed'
            : thresholdStatus === 'unlikely_taxable'
            ? 'Estimated inheritance tax: £0'
            : thresholdStatus === 'likely_taxable' && estimatedIht != null && estimatedIht > 0
                ? `Estimated inheritance tax: ${fmtGBP(estimatedIht)} (approximate)`
                : 'Estimated inheritance tax: To be confirmed';

    const isZeroTax = !unresolved && estimatedIht === 0;

    const estimateSupporting: string =
        unresolved
            ? 'Any tax indication is provisional: trust property that may belong in the estate has not been included.'
            : isZeroTax
            ? 'No inheritance tax appears to be due based on the information entered. The full report confirms this and shows how allowances have been applied.'
            : thresholdStatus === 'likely_taxable'
                ? 'The exact amount depends on allowances, gifts, and trust structures.'
                : 'The final position depends on additional factors not included in this estimate.';

    // Subtle validation prompt for the £0 case — neutral, non-alarmist.
    const validationPrompt: string | null = isZeroTax
        ? 'Small differences in allowances or ownership can affect the final position.'
        : null;

    // ── "Why this matters" — financial stakes (no fabricated numbers) ──
    const whyItMatters =
        unresolved
            ? null
            : thresholdStatus === 'likely_taxable'
            ? 'Inheritance tax can significantly reduce the value passed on to beneficiaries. The exact amount depends on allowances, gifts, and trust structures.'
            : thresholdStatus === 'unlikely_taxable'
                ? null
                : 'The final inheritance tax position depends on several factors, including reliefs and ownership structure.';

    // ── Threshold label (no coloured chip) ────────────────────
    const badgeLabel =
        unresolved
            ? 'Tax position cannot be confirmed'
            : thresholdStatus === 'likely_taxable'
            ? 'May have IHT to pay'
            : thresholdStatus === 'unlikely_taxable'
                ? 'Unlikely to have IHT to pay'
                : 'Tax position to be confirmed';

    // ── Complexity sentence (from backend hints) ──────────────
    const complexityLine = buildComplexityLine(complexityHints);

    return (
        <div style={{ maxWidth: 1200, margin: '24px auto', padding: '0 16px' }} className="flex flex-col gap-4">
            <div className="bg-[#FAFAF8] border border-[#E8E4DB] border-t-2 border-t-[#1A1A1A] px-7 py-6">
                <div className="mb-3">
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#3730A3] font-semibold">
                        {badgeLabel}
                    </span>
                </div>

                <p className="font-serif text-[18px] tracking-[-0.005em] text-[#1A1A1A] leading-relaxed mb-2">
                    {displayHeadline}
                </p>
                {whyItMatters && (
                    <p className="text-[13px] text-[#3F3D39] leading-relaxed mb-5">
                        {whyItMatters}
                    </p>
                )}

                {unresolved && (
                    <div data-testid="free-trust-estate-inclusion-notice" role="note" className="bg-white border border-[#1A1A1A] p-4 mb-4">
                        <p className="font-serif text-[16px] text-[#1A1A1A] m-0">Specialist advice recommended</p>
                        <p className="text-[13px] text-[#3F3D39] mt-1 mb-0 leading-relaxed">
                            Your answers indicate a trust, or uncertainty about one, that may hold property belonging in the estate. That property
                            has not been included in this calculation, so any tax indication is provisional and cannot be confirmed. A trust or
                            inheritance tax specialist should confirm whether the trust property belongs in the estate before you rely on the result.
                        </p>
                    </div>
                )}

                {/* Headline IHT estimate — the free-tier "answer" */}
                <div className="bg-[#EEF0FA] border border-[#C7CBEA] p-4 mb-4">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#3730A3] font-semibold mb-1">
                        Headline estimate
                    </div>
                    <div className="font-serif text-[22px] font-medium tracking-[-0.01em] text-[#3730A3] leading-tight">
                        {estimateLabel}
                    </div>
                    <p className="text-[13px] text-[#3F3D39] mt-2 leading-relaxed">
                        {estimateSupporting}
                    </p>
                    {validationPrompt && (
                        <p className="text-[11px] text-[#6B6762] mt-1.5 leading-relaxed italic">
                            {validationPrompt}
                        </p>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-b border-[#E8E4DB]">
                    <div className="py-[22px] px-6 border-r border-[#E8E4DB]">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-2.5">
                            Gross estate
                        </div>
                        <div className="font-mono tabular-nums text-[22px] text-[#1A1A1A]">
                            {fmtGBP(grossEstateValue)}
                        </div>
                    </div>
                    <div className="py-[22px] px-6">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-[#6B6762] mb-2.5">
                            Net estate
                        </div>
                        <div className="font-mono tabular-nums text-[22px] text-[#1A1A1A]">
                            {fmtGBP(netEstateValue)}
                        </div>
                    </div>
                </div>

                {/* Withheld value signal — no fabricated numbers */}
                <div
                    className="mt-4 flex items-center gap-2 text-[11px] text-[#6B6762] italic"
                    aria-label="Detailed tax calculation locked"
                >
                    <Lock className="w-3.5 h-3.5" />
                    <span>{unresolved
                        ? 'The full report shows the provisional calculation and what is missing'
                        : 'See your exact inheritance tax in the full report'}</span>
                </div>
            </div>

            {/* Contextual complexity / upgrade message */}
            {(complexityLine || upgradeMessage) && (
                <div className="bg-white border border-[#E8E4DB] p-4">
                    {complexityLine && (
                        <p className="text-[13px] text-[#3F3D39] leading-relaxed mb-2">
                            {complexityLine}
                        </p>
                    )}
                    {upgradeMessage && (
                        <p className="text-[13px] text-[#6B6762] leading-relaxed">
                            {upgradeMessage}
                        </p>
                    )}
                    <button
                        onClick={onUpgrade}
                        className="mt-3 bg-[#3730A3] text-white hover:bg-[#2E2780] px-5 py-3 text-[13px] font-medium tracking-[0.02em] transition-colors"
                    >
                        {unresolved
                            ? PROVISIONAL_REPORT_CTA
                            : isZeroTax
                            ? 'Confirm your result and see the full breakdown'
                            : 'See your exact inheritance tax'}
                    </button>
                    <p className="mt-2 text-[11px] text-[#AFA99F] italic">
                        Understand your full position and what you can do next.
                    </p>
                </div>
            )}
        </div>
    );
};

function buildComplexityLine(hints?: ComplexityHints | null): string | null {
    if (!hints) return null;
    const features: string[] = [];
    if (hints.hasTrusts) features.push('trusts');
    if (hints.hasLifetimeGifts) features.push('lifetime gifts');
    if (hints.hasReliefAssets) features.push('business or agricultural property');
    if (hints.hasTenantsInCommon) features.push('tenants-in-common ownership');
    else if (hints.hasMultipleProperties) features.push('multiple properties');
    if (hints.hasForeignAssets) features.push('foreign assets');

    if (features.length === 0) return null;

    const list =
        features.length === 1
            ? features[0]
            : features.length === 2
                ? `${features[0]} and ${features[1]}`
                : `${features.slice(0, -1).join(', ')}, and ${features[features.length - 1]}`;

    return `This estate includes ${list}, which can materially change the final tax position.`;
}
